/**
 * VueXlsx: Vue plugin - v0.6.1 (c) 2020,2023; aburai
 *
 * - write file from array or table element
 * - parse file from drop event
 *
 * @version 0.6.1
 * @author André Bunse (aburai@github.com) <andre.bunse@gmail.com>
 */
import * as XLSX from 'xlsx'
import _ from 'lodash'

const _DEFAULT_FILENAME = 'out.xlsx'
const _VALID_FILETYPES = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain']

const _createSheet = config => {
  if (config.table) return XLSX.utils.table_to_sheet(config.table)
  if (config.labels && config.data) return _createTableSheet(config)
  if (config.data) return XLSX.utils.aoa_to_sheet(config.data)
}

const _createTableSheet = config => {
  const table = document.createElement('table')
  const thead = document.createElement('thead')
  const tr = document.createElement('tr')
  config.labels.forEach(label => {
    const th = document.createElement('th')
    th.innerText = label
    tr.appendChild(th)
  })
  thead.appendChild(tr)
  table.appendChild(thead)
  const tbody = document.createElement('tbody')
  config.data.forEach(row => {
    const tr = document.createElement('tr')
    row.forEach(col => {
      const td = document.createElement('td')
      td.innerText = col
      tr.appendChild(td)
    })
    tbody.appendChild(tr)
  })
  table.appendChild(tbody)
  document.body.appendChild(table)

  const sheet = XLSX.utils.table_to_sheet(table)
  if (config.labels) {
    sheet['!cols'] = config.labels.map((label, i) => {
      return i === 0 ? {width: 30} : {width: 12}
    })
  }

  document.body.removeChild(table)

  return sheet
}

const _readFile = file => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) return reject('invalid argument: not instance of File')
    if (!_VALID_FILETYPES.includes(file.type)) return reject(`invalid file type "${file.type}"`)

    const isPlainText = file.type === 'text/plain'

    try {
      const reader = new FileReader()
      reader.onload = event => {
        if (!isPlainText) {
          const data = new Uint8Array(_.get(event, 'target.result'))
          const workbook = XLSX.read(data, {type: 'array'})
          resolve(workbook)
        }
        else {
          resolve(event.target.result)
        }
      }
      isPlainText ? reader.readAsText(file) : reader.readAsArrayBuffer(file)
    }
    catch (err) {
      reject(err)
    }
  })
}

const VueXlsx = {
  install(Vue, options) {
    const globalOptions = options || {defaultFilename: ''}
    const _defaultFilename = globalOptions.defaultFilename || _DEFAULT_FILENAME

    // write xlsx file from given config
    Vue.prototype.$xlsx = function (config) {
      config = config || {}
      const sheet = _createSheet(config)
      if (!sheet) {
        console.warn('VueXlsx: create sheet failed')
        return
      }

      const filename = config.filename || _defaultFilename

      // sheetRows = 0 (all rows)
      // const workbook = XLSX.utils.table_to_book(table, {sheetRows: 0})
      const workbook = XLSX.utils.book_new()
      workbook.Props = workbook.Props || {}
      if (config.title) workbook.Props.Title = config.title
      workbook.Props.Author = 'VIER Scorecards'

      if (config.colInfo) sheet['!cols'] = config.colInfo
      XLSX.utils.book_append_sheet(workbook, sheet, config.title)

      try {
        XLSX.writeFile(workbook, filename)
      }
      catch (e) {
        console.error('XLSX.writeFile', e)
      }
    }

    // handle drop event and convert valid files to json data
    Vue.prototype.$xlsx.drop = function (event) {
      if (!(event instanceof DragEvent)) return Promise.reject('invalid event type')

      event.stopPropagation()
      event.preventDefault()

      // TODO handle more than one file?
      const file = event.dataTransfer.files[0]
      return _readFile(file).then(wb => {
        if (typeof wb === 'string') return {text: wb, filename: file.name} // plain text

        const sheets = []
        _.each(wb.Sheets, sheet => {
          sheets.push(XLSX.utils.sheet_to_json(sheet))
        })
        return {sheets, filename: file.name}
      })
    }
  }
}

export default VueXlsx

if (typeof window !== 'undefined' && window.Vue) window.Vue.use(VueXlsx)
